import { useState, useEffect } from "react";
import axios from "axios";
import { ShoppingCart, Zap, Gift, BookOpen, Package, Star, Check, Truck } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const TYPE_ICON = {
  physical: <Package className="w-5 h-5" />,
  physical_no_ship: <Package className="w-5 h-5" />,
  digital: <Zap className="w-5 h-5" />,
  membership: <Gift className="w-5 h-5" />,
};

const PRODUCT_FEATURES = {
  single_book: ["Physical hardcopy book", "Shipped to your door", "The full story & Success Pipeline", "Ages 6-14"],
  single_book_paid: ["Already received book", "Pay without shipping", "Supports the movement", "Clear your conscience"],
  gift_pair: ["One for you", "One to gift forward", "Shipped together", "Create ripple effect"],
  digital_book: ["Instant access after payment", "Interactive iPad reader", "Read on any device", "No waiting — start now"],
  gift_starter: ["10 physical hardcopy books", "FREE shipping included", "Next book FREE forever", "Gift to 9 other families"],
  gift_champion: ["25 physical hardcopy books", "FREE shipping always", "Next 2 books FREE", "Perfect for classrooms"],
  lifetime_member: ["50 physical hardcopy books", "FREE shipping forever", "ALL future books FREE", "Unlimited lifetime impact"],
};

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart, cartItems } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/products`).then(res => {
      setProducts(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const isInCart = (id) => cartItems.some(i => i.product_id === id);

  const handleAddToCart = (product) => {
    if (product.type === "digital" && !user) {
      navigate("/login");
      return;
    }
    addToCart(product);
  };

  const getProduct = (id) => products.find(p => p.id === id);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FDFBF7" }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2F5233] mx-auto mb-4"></div>
          <p className="text-[#5A5A5A]">Loading products...</p>
        </div>
      </div>
    );
  }

  const single = getProduct("single_book");
  const singlePaid = getProduct("single_book_paid");
  const giftPair = getProduct("gift_pair");
  const digital = getProduct("digital_book");
  const starter = getProduct("gift_starter");
  const champion = getProduct("gift_champion");
  const lifetime = getProduct("lifetime_member");

  return (
    <div className="min-h-screen py-12" style={{ backgroundColor: "#FDFBF7" }} data-testid="shop-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 text-center">
        <div className="inline-flex items-center gap-2 bg-[#2F5233]/10 text-[#2F5233] px-4 py-2 rounded-full text-sm font-semibold mb-4">
          <BookOpen className="w-4 h-4" /> Choose Your Path
        </div>
        <h1 className="text-5xl font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
          Get Your Books Today
        </h1>
        <p className="text-lg text-[#5A5A5A] max-w-2xl mx-auto">
          Every option delivers the same powerful content. Choose what fits your goals — from a single book to lifetime membership.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Book Image Banner */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-8 bg-white rounded-3xl p-6 border border-[#E6E2D3] shadow-sm w-full max-w-2xl">
              <img
                src="/images/book-cover.jpg"
                alt="Expect to Win Kids Edition"
                className="w-32 h-32 object-cover rounded-2xl shadow-md flex-shrink-0"
              />
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-[#F4D03F] text-[#1A1A1A] text-xs font-bold px-2 py-0.5 rounded-full">BEST SELLER</span>
                  <span className="bg-[#D2691E] text-white text-xs font-bold px-2 py-0.5 rounded-full">Endorsed by Monte Holm</span>
                </div>
                <h2 className="text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Playfair Display', serif" }}>Expect to Win — Kids Edition</h2>
                <p className="text-[#5A5A5A] text-sm mt-1">Illustrated & created by <strong>Kyle Keith</strong></p>
                <p className="text-[#5A5A5A] text-sm">The 6-Step Success Pipeline · Ages 6–14</p>
              </div>
            </div>
          </div>

        {/* Bento Grid Row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Single Book - Large */}
          {single && (
            <div className="md:col-span-2 bg-white border border-[#E6E2D3] rounded-3xl p-8 hover:border-[#2F5233]/30 transition-all hover:shadow-lg" data-testid="product-single_book">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-[#2F5233]/10 rounded-2xl flex items-center justify-center text-[#2F5233]">
                  <Package className="w-6 h-6" />
                </div>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-[#F4D03F] fill-[#F4D03F]" />)}
                </div>
              </div>
              <h3 className="text-2xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>1 Physical Book</h3>
              <p className="text-[#5A5A5A] mb-4">One hardcopy of <em>Expect to Win Kids Edition</em> shipped directly to your door.</p>
              <div className="grid grid-cols-2 gap-2 mb-6">
                {PRODUCT_FEATURES.single_book.map(f => (
                  <div key={f} className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                    <Check className="w-4 h-4 text-[#2F5233] flex-shrink-0" />{f}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-3xl font-bold text-[#2F5233]">${single.price.toFixed(2)}</span>
                  <span className="text-[#5A5A5A] text-sm ml-2">+ $3.95 shipping</span>
                </div>
                <button
                  onClick={() => handleAddToCart(single)}
                  disabled={isInCart("single_book")}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all hover:scale-105 ${isInCart("single_book") ? "bg-[#B2D3C2] text-[#2F5233] cursor-not-allowed" : "bg-[#2F5233] text-white hover:bg-[#1E3A21]"}`}
                  data-testid="add-single_book"
                >
                  <ShoppingCart className="w-4 h-4" />
                  {isInCart("single_book") ? "In Cart" : "Add to Cart"}
                </button>
              </div>
            </div>
          )}

          {/* Digital Book */}
          {digital && (
            <div className="bg-gradient-to-br from-[#2F5233] to-[#4A7C52] border border-[#2F5233] rounded-3xl p-8 text-white hover:shadow-lg transition-all hover:-translate-y-1" data-testid="product-digital_book">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                  <Zap className="w-6 h-6" />
                </div>
                <span className="bg-[#F4D03F] text-[#1A1A1A] text-xs font-bold px-3 py-1 rounded-full">Instant Access</span>
              </div>
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Digital Book</h3>
              <p className="text-white/80 text-sm mb-4">Interactive reader for iPad & all devices. Access immediately after payment.</p>
              <div className="space-y-2 mb-6">
                {PRODUCT_FEATURES.digital_book.map(f => (
                  <div key={f} className="flex items-center gap-2 text-sm text-white/80">
                    <Check className="w-4 h-4 text-[#B2D3C2] flex-shrink-0" />{f}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-auto">
                <div>
                  <span className="text-3xl font-bold">${digital.price.toFixed(2)}</span>
                  <span className="text-white/60 text-sm ml-1">only</span>
                </div>
                <button
                  onClick={() => handleAddToCart(digital)}
                  disabled={isInCart("digital_book")}
                  className={`flex items-center gap-2 px-5 py-3 rounded-full font-bold transition-all hover:scale-105 ${isInCart("digital_book") ? "bg-white/30 cursor-not-allowed" : "bg-[#F4D03F] text-[#1A1A1A] hover:bg-[#e8c91e]"}`}
                  data-testid="add-digital_book"
                >
                  <ShoppingCart className="w-4 h-4" />
                  {isInCart("digital_book") ? "In Cart" : "Add to Cart"}
                </button>
              </div>
              {!user && <p className="text-white/60 text-xs mt-2">Account required for digital access</p>}
            </div>
          )}
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Gift Pair - Most Popular */}
          {giftPair && (
            <div className="md:col-span-2 bg-[#FFF8E7] border-2 border-[#D2691E] rounded-3xl p-8 hover:shadow-lg transition-all relative overflow-hidden" data-testid="product-gift_pair">
              <div className="absolute top-4 right-4 bg-[#D2691E] text-white text-xs font-bold px-3 py-1.5 rounded-full">Most Popular</div>
              <div className="w-12 h-12 bg-[#D2691E]/10 rounded-2xl flex items-center justify-center text-[#D2691E] mb-4">
                <Gift className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>2 Books — Gift it Forward</h3>
              <p className="text-[#5A5A5A] mb-4">One for you, one to gift forward to another family. Be the reason a child WINs.</p>
              <div className="grid grid-cols-2 gap-2 mb-6">
                {PRODUCT_FEATURES.gift_pair.map(f => (
                  <div key={f} className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                    <Check className="w-4 h-4 text-[#D2691E] flex-shrink-0" />{f}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-3xl font-bold text-[#D2691E]">${giftPair.price.toFixed(2)}</span>
                  <span className="text-[#5A5A5A] text-sm ml-2">+ $3.95 shipping</span>
                </div>
                <button
                  onClick={() => handleAddToCart(giftPair)}
                  disabled={isInCart("gift_pair")}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all hover:scale-105 ${isInCart("gift_pair") ? "bg-[#D2691E]/30 text-[#D2691E] cursor-not-allowed" : "bg-[#D2691E] text-white hover:bg-[#A0521A]"}`}
                  data-testid="add-gift_pair"
                >
                  <ShoppingCart className="w-4 h-4" />
                  {isInCart("gift_pair") ? "In Cart" : "Add to Cart"}
                </button>
              </div>
            </div>
          )}

          {/* Already Received */}
          {singlePaid && (
            <div className="bg-white border border-[#E6E2D3] rounded-3xl p-8 hover:border-[#2F5233]/30 transition-all hover:shadow-lg" data-testid="product-single_book_paid">
              <div className="w-12 h-12 bg-[#B2D3C2] rounded-2xl flex items-center justify-center text-[#2F5233] mb-4">
                <BookOpen className="w-6 h-6" />
              </div>
              <span className="bg-[#B2D3C2] text-[#2F5233] text-xs font-bold px-3 py-1 rounded-full">No Shipping</span>
              <h3 className="text-xl font-bold text-[#1A1A1A] mt-3 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Already Received</h3>
              <p className="text-[#5A5A5A] text-sm mb-4">Already have your book? Pay for it here — no shipping fee.</p>
              <div className="space-y-2 mb-6">
                {PRODUCT_FEATURES.single_book_paid.map(f => (
                  <div key={f} className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                    <Check className="w-4 h-4 text-[#2F5233] flex-shrink-0" />{f}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-[#2F5233]">${singlePaid.price.toFixed(2)}</span>
                <button
                  onClick={() => handleAddToCart(singlePaid)}
                  disabled={isInCart("single_book_paid")}
                  className={`flex items-center gap-2 px-5 py-3 rounded-full font-bold transition-all hover:scale-105 text-sm ${isInCart("single_book_paid") ? "bg-[#B2D3C2] text-[#2F5233] cursor-not-allowed" : "bg-[#2F5233] text-white hover:bg-[#1E3A21]"}`}
                  data-testid="add-single_book_paid"
                >
                  <ShoppingCart className="w-4 h-4" />
                  {isInCart("single_book_paid") ? "In Cart" : "Add to Cart"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Membership Tiers - Full Width */}
        <div className="bg-[#2F5233] rounded-3xl p-8 mb-8" data-testid="membership-section">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Truck className="w-4 h-4" /> Free Shipping Included
            </div>
            <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Gift it Forward Memberships
            </h2>
            <p className="text-[#B2D3C2]">One-time payment. Maximum impact. Free shipping on all bundles.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { product: starter, key: "gift_starter", highlight: false },
              { product: champion, key: "gift_champion", highlight: true },
              { product: lifetime, key: "lifetime_member", highlight: false },
            ].map(({ product, key, highlight }) => product && (
              <div key={key} className={`rounded-2xl p-6 ${highlight ? 'bg-[#F4D03F]' : 'bg-white/10 border border-white/20'}`} data-testid={`product-${key}`}>
                {product.badge && (
                  <span className={`text-xs font-bold px-3 py-1 rounded-full inline-block mb-3 ${highlight ? 'bg-[#D2691E] text-white' : 'bg-white/20 text-white'}`}>
                    {product.badge}
                  </span>
                )}
                <div className={`text-3xl font-bold mb-1 ${highlight ? 'text-[#1A1A1A]' : 'text-white'}`} style={{ fontFamily: "'Playfair Display', serif" }}>
                  ${product.price.toFixed(2)}
                </div>
                <div className={`font-bold text-lg mb-1 ${highlight ? 'text-[#1A1A1A]' : 'text-white'}`}>{product.books_count} Books</div>
                <p className={`text-sm mb-4 leading-relaxed ${highlight ? 'text-[#5A5A5A]' : 'text-white/70'}`}>{product.description}</p>
                <div className="space-y-2 mb-6">
                  {PRODUCT_FEATURES[key]?.map(f => (
                    <div key={f} className={`flex items-center gap-2 text-sm ${highlight ? 'text-[#5A5A5A]' : 'text-white/80'}`}>
                      <Check className={`w-4 h-4 flex-shrink-0 ${highlight ? 'text-[#2F5233]' : 'text-[#B2D3C2]'}`} />{f}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => handleAddToCart(product)}
                  disabled={isInCart(key)}
                  className={`w-full flex items-center justify-center gap-2 py-3 rounded-full font-bold transition-all hover:scale-105 ${
                    isInCart(key)
                      ? (highlight ? "bg-[#D2691E]/30 text-[#D2691E] cursor-not-allowed" : "bg-white/20 text-white/50 cursor-not-allowed")
                      : (highlight ? "bg-[#2F5233] text-white hover:bg-[#1E3A21]" : "bg-[#F4D03F] text-[#1A1A1A] hover:bg-[#e8c91e]")
                  }`}
                  data-testid={`add-${key}`}
                >
                  <ShoppingCart className="w-4 h-4" />
                  {isInCart(key) ? "In Cart" : "Add to Cart"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
